import React from 'react';
import { FileText, CheckSquare, AlertTriangle } from 'lucide-react';

/**
 * Компонент для выбора российских стандартов типографики
 */
const RussianStandardsSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  russianStandards 
}) => {
  return (
    <div className="mb-6 bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-100 dark:border-purple-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <FileText className="w-5 h-5 mr-2 text-purple-500" />
        Российские стандарты
      </h3>
      
      <div className="space-y-4">
        {/* Выбор стандарта */}
        <div>
          <label htmlFor="russianStandard" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Применяемый стандарт
          </label>
          <select
            id="russianStandard"
            name="russianStandard"
            value={formData.russianStandard}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            <option value="">Не выбран</option>
            {russianStandards.map((standard) => (
              <option key={standard.id} value={standard.id}>
                {standard.name} - {standard.desc}
              </option>
            ))}
          </select>
          {errors.russianStandard && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.russianStandard}
            </p>
          )}
        </div>

        {/* Информация о выбранном стандарте */}
        {formData.russianStandard && (
          <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded-lg border border-purple-200 dark:border-purple-800">
            <div className="flex items-start space-x-3">
              <CheckSquare className="w-5 h-5 text-purple-500 dark:text-purple-400 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-1">
                  {russianStandards.find(s => s.id === formData.russianStandard)?.name}
                </h4>
                <p className="text-xs text-purple-600 dark:text-purple-400 mb-1">
                  {getStandardDetails(formData.russianStandard)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Информационная панель о соответствии требованиям */}
        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg border border-yellow-100 dark:border-yellow-800">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-yellow-500 dark:text-yellow-400 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-yellow-700 dark:text-yellow-300 mb-1">
                Важно знать о российских нормативах
              </h4>
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mb-1">
                В России существуют строгие требования к типографике в документах и на веб-сайтах, 
                особенно для государственных учреждений, образовательных организаций и медицинских сайтов.
              </p>
              <p className="text-xs text-yellow-600 dark:text-yellow-400">
                Несоблюдение норм может привести к штрафам согласно законодательству РФ, 
                особенно при нарушении требований к версиям сайтов для слабовидящих.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Получение детальной информации о стандарте
 * @param {string} standardId ID стандарта
 * @returns {string} Детальная информация
 */
const getStandardDetails = (standardId) => {
  switch (standardId) {
    case 'GOST-R-54723':
      return 'ГОСТ Р 54723-2019 определяет требования к электронным изданиям и интернет-ресурсам. ' +
             'Для веб-сайтов минимальный размер основного текста должен быть не менее 12px, ' + 
             'для мобильных устройств — не менее 14px. Рекомендуется использовать ' +
             'шрифты без засечек для экранного чтения.';
    
    case 'GOST-R-58511':
      return 'ГОСТ Р 58511-2019 устанавливает требования к оформлению печатных и электронных изданий. ' +
             'Для электронных документов рекомендуется использовать шрифты размером от 12 до 16 пунктов ' +
             'в зависимости от типа издания и целевой аудитории. Для детской аудитории минимальный ' +
             'размер шрифта составляет 16 пунктов.';
    
    case 'GOST-P-57850':
      return 'ГОСТ Р 57850-2017 регламентирует требования к оформлению веб-сайтов для людей с нарушениями зрения. ' +
             'Требуется поддержка увеличения шрифта минимум до 200%, контрастность текста не менее 7:1, ' +
             'минимальный размер шрифта 18px, межстрочный интервал не менее 1.5.';
    
    case 'SanPin':
      return 'СанПиН 1.2.3685-21 устанавливает гигиенические нормативы для электронных изданий, ' +
             'в том числе для детей и подростков. Определяет минимальные размеры шрифта в зависимости ' +
             'от возрастных групп: для дошкольников — 14-18pt, для школьников 12-14pt, ' +
             'для пользователей старшего возраста рекомендовано не менее 12pt.';
    
    default:
      return 'Выберите российский стандарт для получения подробной информации о требованиях.';
  }
};

export default RussianStandardsSection;